import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useParams } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import ProtectedLayout from "@/components/ProtectedLayout";
import { RoleGate } from "@/components/RoleGate";
import NotFound from "@/pages/NotFound";
import DocsStmBudget from "@/pages/DocsStmBudget";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Groups from "@/pages/Groups";
import GroupsBilling from "@/pages/GroupsBilling";
import Members from "@/pages/Members";
import Sources from "@/pages/Sources";
import UsersAdmin from "@/pages/UsersAdmin";
import SettingsPage from "@/pages/Settings";
import Integrations from "@/pages/Integrations";
import Logs from "@/pages/Logs";
import MediaLogs from "@/pages/MediaLogs";
import MediaHistory from "@/pages/MediaHistory";
import PromptLibrary from "@/pages/PromptLibrary";
import Agents from "@/pages/Agents";
import AgentPersonality from "@/pages/AgentPersonality";
// AIProviders embedded inside ModelRoutingHub
import Commands from "@/pages/Commands";
// NotFound imported above
// Health/HallucinationHealth/FactualHealth/ModelsHealth/ProvidersHealth são tabs dentro de HealthHub —
// rotas legadas redirecionam para /admin/health?tab=...; não importar aqui (MS-15: dead imports).
import Organizations from "@/pages/Organizations";
import Skills from "@/pages/Skills";
import SkillsV19 from "@/pages/SkillsV19";
import SkillDetail from "@/pages/SkillDetail";
import SkillLab from "@/pages/SkillLab";
import SkillsObservability from "@/pages/SkillsObservability";
import Usage from "@/pages/Usage";
import Billing from "@/pages/Billing";
import ResetPassword from "@/pages/ResetPassword";
import Support from "@/pages/Support";
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import Contact from "@/pages/Contact";
import Conversations from "@/pages/Conversations";
import Intelligence from "@/pages/Intelligence";
import Performance from "@/pages/Performance";
import Security from "@/pages/Security";
import Sentinel from "@/pages/Sentinel";
import SalesAssistant from "@/pages/SalesAssistant";
import Copilot from "@/pages/Copilot";
import Onboarding from "@/pages/Onboarding";
import Privacy from "@/pages/Privacy";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import BlogAdmin from "@/pages/BlogAdmin";
import DataManagement from "@/pages/DataManagement";
import ContactAdmin from "@/pages/ContactAdmin";
import KnowledgeBase from "@/pages/KnowledgeBase";
import Status from "@/pages/Status";
import AdminPanel from "@/pages/AdminPanel";
import JarvisHub from "@/pages/JarvisHub";
import JarvisCreateSession from "@/pages/JarvisCreateSession";
import JarvisHistory from "@/pages/JarvisHistory";
import JarvisLiveSession from "@/pages/JarvisLiveSession";
import JarvisSessionReview from "@/pages/JarvisSessionReview";
import HealthHub from "@/pages/admin/HealthHub";
import Glossario from "@/pages/admin/Glossario";
import JarvisJoin from "@/pages/JarvisJoin";
import CortexOSHub from "@/pages/CortexOSHub";
import CortexLiveCards from "@/pages/CortexLiveCards";
import WikiHub from "@/pages/WikiHub";
import CortexMultimodal from "@/pages/CortexMultimodal";
import MediaIA from "@/pages/MediaIA";
import ModelRoutingHub from "@/pages/ModelRoutingHub";
import MemoryHub from "@/pages/MemoryHub";
import MemoryDetail from "@/pages/MemoryDetail";
// MemoryHealth consolidado em /memory?tab=health (ver MemoryHub)
import GovernancaForense from "@/pages/GovernancaForense";
import SistemaFlags from "@/pages/SistemaFlags";
import SistemaCusto from "@/pages/SistemaCusto";
import SistemaLatencia from "@/pages/SistemaLatencia";
import SistemaProntidaoComercial from "@/pages/SistemaProntidaoComercial";

import AlertsAndSlos from "@/pages/admin/AlertsAndSlos";
import SystemHealth from "@/pages/admin/SystemHealth";
import TomInsights from "@/pages/admin/TomInsights";
import MediaInsights from "@/pages/admin/MediaInsights";
import QualityDashboardAdmin from "@/pages/admin/QualityDashboard";
import Phase3Readiness from "@/pages/admin/Phase3Readiness";
import ScheduledSkillsManager from "@/pages/admin/ScheduledSkillsManager";
import WMComposerInsights from "@/pages/admin/WMComposerInsights";
import PeopleMemoryPage from "@/pages/admin/PeopleMemory";
import MemoryMetricsPage from "@/pages/admin/MemoryMetrics";
import LtmDashboard from "@/pages/admin/LtmDashboard";
import CortexHealth from "@/pages/admin/CortexHealth";
import PipelineErrors from "@/pages/admin/PipelineErrors";
import OutboundChunks from "@/pages/admin/OutboundChunks";
import FlagAudit from "@/pages/admin/FlagAudit";
import DevConsoleGroups from "@/pages/admin/DevConsoleGroups";
import Reminders from "@/pages/group/Reminders";
import Commitments from "@/pages/group/Commitments";
import LinkLibrary from "@/pages/group/LinkLibrary";
import TurnInspector from "@/pages/TurnInspector";
import Polls from "@/pages/group/Polls";
import GroupBlacklist from "@/pages/group/GroupBlacklist";
import GroupMentalMap from "@/pages/group/GroupMentalMap";
import Sandbox from "@/pages/Sandbox";
import ConversationReplay from "@/pages/ConversationReplay";
import Operacao from "@/pages/Operacao";
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Sprint D: alias /live/:id → /jarvis/session/:id
function JarvisLiveSessionRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/jarvis/session/${id}`} replace />;
}

// OnboardingGuard removed — onboarding redirect is handled at /onboarding route level

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/status" element={<Status />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/onboarding" element={isAuthenticated ? <Onboarding /> : <Navigate to="/login" replace />} />
      <Route path="/dashboard" element={<ProtectedLayout><Dashboard /></ProtectedLayout>} />
      <Route path="/groups" element={<ProtectedLayout><Groups /></ProtectedLayout>} />
      <Route path="/groups/billing" element={<ProtectedLayout><GroupsBilling /></ProtectedLayout>} />
      <Route path="/members" element={<ProtectedLayout><Members /></ProtectedLayout>} />
      <Route path="/sources" element={<ProtectedLayout><Sources /></ProtectedLayout>} />
      <Route path="/prompt-library" element={<ProtectedLayout><PromptLibrary /></ProtectedLayout>} />
      <Route path="/prompts" element={<Navigate to="/prompt-library" replace />} />
      <Route path="/agents" element={<ProtectedLayout><Agents /></ProtectedLayout>} />
      <Route path="/agents/:id/personality" element={<ProtectedLayout><AgentPersonality /></ProtectedLayout>} />
      {/* /ai-providers redirected below to /model-routing?tab=providers */}
      <Route path="/commands" element={<ProtectedLayout><Commands /></ProtectedLayout>} />
      <Route path="/logs" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><Logs /></RoleGate></ProtectedLayout>} />
      <Route path="/media-logs" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><MediaLogs /></RoleGate></ProtectedLayout>} />
      <Route path="/media-history" element={<ProtectedLayout><MediaHistory /></ProtectedLayout>} />
      <Route path="/media-ia" element={<ProtectedLayout><MediaIA /></ProtectedLayout>} />
      <Route path="/users" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><UsersAdmin /></RoleGate></ProtectedLayout>} />
      <Route path="/settings" element={<ProtectedLayout><SettingsPage /></ProtectedLayout>} />
      <Route path="/settings/integrations" element={<ProtectedLayout><Integrations /></ProtectedLayout>} />
      <Route path="/health" element={<Navigate to="/admin/health?tab=geral" replace />} />
      <Route path="/organizations" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><Organizations /></RoleGate></ProtectedLayout>} />
      <Route path="/skills" element={<ProtectedLayout><SkillsV19 /></ProtectedLayout>} />
      <Route path="/skills/legacy" element={<ProtectedLayout><Skills /></ProtectedLayout>} />
      <Route path="/skills/lab" element={<ProtectedLayout><SkillLab /></ProtectedLayout>} />
      <Route path="/skills/observability" element={<ProtectedLayout><SkillsObservability /></ProtectedLayout>} />
      <Route path="/sandbox" element={<ProtectedLayout><Sandbox /></ProtectedLayout>} />
      <Route path="/replay" element={<ProtectedLayout><ConversationReplay /></ProtectedLayout>} />
      <Route path="/skills/:id" element={<ProtectedLayout><SkillDetail /></ProtectedLayout>} />
      <Route path="/pipeline" element={<Navigate to="/cortex-os?tab=pipeline" replace />} />
      <Route path="/quality" element={<Navigate to="/admin/quality-dashboard" replace />} />
      <Route path="/quality-dashboard" element={<Navigate to="/admin/quality-dashboard" replace />} />
      <Route path="/intents" element={<Navigate to="/cortex-os?tab=intents" replace />} />
      <Route path="/routing-config" element={<Navigate to="/model-routing?tab=config" replace />} />
      <Route path="/model-policies" element={<Navigate to="/model-routing?tab=policies" replace />} />
      <Route path="/model-decisions" element={<Navigate to="/model-routing?tab=decisions" replace />} />
      <Route path="/ai-providers" element={<Navigate to="/model-routing?tab=providers" replace />} />
      <Route path="/realtime-flow" element={<Navigate to="/cortex-os?tab=fluxo" replace />} />
      <Route path="/cortex-os" element={<ProtectedLayout><CortexOSHub /></ProtectedLayout>} />
      <Route path="/cortex-os/live" element={<ProtectedLayout><CortexLiveCards /></ProtectedLayout>} />
      <Route path="/cortex-os/turn/:id" element={<ProtectedLayout><TurnInspector /></ProtectedLayout>} />
      <Route path="/hallucination" element={<Navigate to="/admin/health?tab=alucinacao" replace />} />
      <Route path="/admin/cortex/multimodal" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><CortexMultimodal /></RoleGate></ProtectedLayout>} />
      <Route path="/operacao" element={<ProtectedLayout><Operacao /></ProtectedLayout>} />
      <Route path="/operacao/avancada" element={<Navigate to="/cortex-os?tab=avancada" replace />} />
      <Route path="/governanca/forense" element={<ProtectedLayout><GovernancaForense /></ProtectedLayout>} />
      <Route path="/governanca/regulacao" element={<Navigate to="/governanca/forense" replace />} />
      <Route path="/memoria/auditoria" element={<Navigate to="/memory?tab=auditoria" replace />} />
      <Route path="/usage" element={<ProtectedLayout><Usage /></ProtectedLayout>} />
      <Route path="/billing" element={<ProtectedLayout><Billing /></ProtectedLayout>} />
      <Route path="/support" element={<ProtectedLayout><Support /></ProtectedLayout>} />
      <Route path="/conversations" element={<ProtectedLayout><Conversations /></ProtectedLayout>} />
      <Route path="/memory" element={<ProtectedLayout><MemoryHub /></ProtectedLayout>} />
      <Route path="/wiki" element={<ProtectedLayout><WikiHub /></ProtectedLayout>} />
      <Route path="/memory/:id" element={<ProtectedLayout><MemoryDetail /></ProtectedLayout>} />
      <Route path="/memory-health" element={<Navigate to="/memory?tab=health" replace />} />
      <Route path="/model-routing" element={<ProtectedLayout><ModelRoutingHub /></ProtectedLayout>} />
      {/* Health Hub consolidado (Auditoria R2) — 7 páginas → 1 com tabs */}
      <Route path="/admin/health" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><HealthHub /></RoleGate></ProtectedLayout>} />
      <Route path="/admin/models/health" element={<Navigate to="/admin/health?tab=modelos" replace />} />
      <Route path="/admin/providers/health" element={<Navigate to="/admin/health?tab=provedores" replace />} />
      <Route path="/admin/system-health" element={<Navigate to="/admin/health?tab=sistema" replace />} />
      <Route path="/admin/factual/health" element={<Navigate to="/admin/health?tab=factual" replace />} />
      <Route path="/cortex-os/hallucination" element={<Navigate to="/admin/health?tab=alucinacao" replace />} />
      <Route path="/admin/alerts-slos" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><AlertsAndSlos /></RoleGate></ProtectedLayout>} />
      <Route path="/admin/tom-insights" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><TomInsights /></RoleGate></ProtectedLayout>} />
      <Route path="/tom-insights" element={<Navigate to="/admin/tom-insights" replace />} />
      <Route path="/admin/media-insights" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><MediaInsights /></RoleGate></ProtectedLayout>} />
      
      <Route path="/admin/quality-dashboard" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><QualityDashboardAdmin /></RoleGate></ProtectedLayout>} />
      <Route path="/admin/phase3-readiness" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><Phase3Readiness /></RoleGate></ProtectedLayout>} />
      <Route path="/admin/scheduled-skills" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><ScheduledSkillsManager /></RoleGate></ProtectedLayout>} />
      <Route path="/admin/wm-composer" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><WMComposerInsights /></RoleGate></ProtectedLayout>} />
      <Route path="/admin/people-memory" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><PeopleMemoryPage /></RoleGate></ProtectedLayout>} />
      <Route path="/admin/memory-metrics" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><MemoryMetricsPage /></RoleGate></ProtectedLayout>} />
      <Route path="/admin/ltm-dashboard" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><LtmDashboard /></RoleGate></ProtectedLayout>} />
      <Route path="/admin/cortex-health" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><CortexHealth /></RoleGate></ProtectedLayout>} />
      <Route path="/admin/pipeline-errors" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><PipelineErrors /></RoleGate></ProtectedLayout>} />
      <Route path="/admin/outbound-chunks" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><OutboundChunks /></RoleGate></ProtectedLayout>} />
      <Route path="/admin/flags-audit" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><FlagAudit /></RoleGate></ProtectedLayout>} />
      <Route path="/admin/dev-console-groups" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><DevConsoleGroups /></RoleGate></ProtectedLayout>} />
      <Route path="/reminders" element={<ProtectedLayout><Reminders /></ProtectedLayout>} />
      <Route path="/commitments" element={<ProtectedLayout><Commitments /></ProtectedLayout>} />
      <Route path="/link-library" element={<ProtectedLayout><LinkLibrary /></ProtectedLayout>} />
      <Route path="/polls" element={<ProtectedLayout><Polls /></ProtectedLayout>} />
      <Route path="/groups/:groupId/blacklist" element={<ProtectedLayout><GroupBlacklist /></ProtectedLayout>} />
      <Route path="/groups/:groupId/mental-map" element={<ProtectedLayout><GroupMentalMap /></ProtectedLayout>} />
      <Route path="/performance" element={<ProtectedLayout><Performance /></ProtectedLayout>} />
      <Route path="/security" element={<ProtectedLayout><Security /></ProtectedLayout>} />
      <Route path="/sentinel" element={<ProtectedLayout><Sentinel /></ProtectedLayout>} />
      <Route path="/sales" element={<ProtectedLayout><SalesAssistant /></ProtectedLayout>} />
      <Route path="/copilot" element={<ProtectedLayout><Copilot /></ProtectedLayout>} />
      <Route path="/feature-flags" element={<Navigate to="/sistema/flags" replace />} />
      <Route path="/sistema/flags" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><SistemaFlags /></RoleGate></ProtectedLayout>} />
      <Route path="/sistema/custo" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><SistemaCusto /></RoleGate></ProtectedLayout>} />
      <Route path="/sistema/latencia" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><SistemaLatencia /></RoleGate></ProtectedLayout>} />
      <Route path="/sistema/prontidao-comercial" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><SistemaProntidaoComercial /></RoleGate></ProtectedLayout>} />
      <Route path="/sistema/v14-rollout" element={<Navigate to="/admin" replace />} />
      <Route path="/sistema/limpeza-ui" element={<Navigate to="/admin" replace />} />
      <Route path="/sistema/glossario" element={<Navigate to="/admin/glossario" replace />} />
      <Route path="/admin/glossario" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><Glossario /></RoleGate></ProtectedLayout>} />
      <Route path="/memoria/quarentena" element={<Navigate to="/memory?tab=quarentena" replace />} />
      <Route path="/data-management" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><DataManagement /></RoleGate></ProtectedLayout>} />
      <Route path="/blog-admin" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><BlogAdmin /></RoleGate></ProtectedLayout>} />
      <Route path="/contacts-admin" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><ContactAdmin /></RoleGate></ProtectedLayout>} />
      <Route path="/knowledge-base" element={<ProtectedLayout><KnowledgeBase /></ProtectedLayout>} />
      <Route path="/docs/stm-budget" element={<ProtectedLayout><DocsStmBudget /></ProtectedLayout>} />
      <Route path="/admin" element={<ProtectedLayout><RoleGate permission="admin.panel" fallback={<NotFound />}><AdminPanel /></RoleGate></ProtectedLayout>} />
      <Route path="/jarvis" element={<ProtectedLayout><JarvisHub /></ProtectedLayout>} />
      <Route path="/jarvis/new" element={<ProtectedLayout><JarvisCreateSession /></ProtectedLayout>} />
      <Route path="/jarvis/history" element={<ProtectedLayout><JarvisHistory /></ProtectedLayout>} />
      <Route path="/jarvis/session/:id" element={<ProtectedLayout><JarvisLiveSession /></ProtectedLayout>} />
      <Route path="/jarvis/session/:id/review" element={<ProtectedLayout><JarvisSessionReview /></ProtectedLayout>} />
      <Route path="/jarvis/settings" element={<Navigate to="/jarvis?tab=settings" replace />} />
      <Route path="/jarvis/telemetry" element={<Navigate to="/jarvis?tab=telemetry" replace />} />
      {/* Sprint D: short-aliases for Jarvis standalone */}
      <Route path="/live/new" element={<Navigate to="/jarvis/new" replace />} />
      <Route path="/live/:id" element={<JarvisLiveSessionRedirect />} />
      <Route path="/join/:token" element={<JarvisJoin />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
